import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
  signal
} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Sort} from '@angular/material/sort';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {DateUtil} from '../../../../common/util/DateUtil';
import {ClaimService} from '../../../services/claim.service';
import {ClaimDisplayDTO} from '../../../models/claim/ClaimDisplayDTO';
import {TableControl} from '../../../../common/models/TableControl';
import {PagedRequestDTO} from '../../../../common/models/PagedRequestDTO';
import {PagedFilterDTO, PagedFilterDTONew} from '../../../../common/models/PagedFilterDTO';
import {Router} from '@angular/router';
import {ActivityHistoryDialogComponent} from '../../dialogs/activity-history-dialog/activity-history-dialog.component';
import {LookupService} from '../../../../common/services/lookup.service';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {SharedService} from '../../../../common/services/shared-service';
import {defer, EMPTY, forkJoin, iif, Observable, of, Subject} from 'rxjs';
import {catchError, debounceTime, mapTo, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {
  Common,
  getPaginatorLengthFromPagedResponse,
  getPastDateFromToday,
  getSortDirection,
  getStatusColor
} from '../../../../common/util/Util';
import {DatePipe} from '@angular/common';
import {
  ConfirmationDialogComponent
} from '../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {WorkflowResultDTO} from '../../../models/WorkflowResultDTO';
import {TimeElapsedFromNowPipe} from '../../../../common/pipes/time-elapsed-from-now.pipe';
import {ClmFunctions, isValidCommaSeparatedNumberList, PayToAcronymEnum} from '../../../common/functions/clm.functions';
import {ClaimType} from '../../../types/claim-type';
import {ClaimFilterCacheService, ClaimFilterKeys, ClaimFilterType} from '../../../services/claim-filter-cache.service';
import {detailsExpand} from '../../../../common';
import {AdjudicationStatus} from '../../../types/adjudication-status.enum';
import {ProcessingStatus} from '../../../types/processing-status.enum';
import {
  AddManualPaymentDialogComponent
} from '../../dialogs/add-manual-payment-dialog/add-manual-payment-dialog.component';
import {PagedResponseDTO} from '../../../../common/models/PagedResponseDTO';
import {ClaimExcelSheetService} from '../../../services/claim-excel-sheet.service';
import {ProcessingClass, ProcessingClassDisplayValue} from '../../../types/processing-class.enum';
import {TabLabel} from '../../../../common/types';
import {UserAuthorisationService} from 'src/app/modules/common/services/user-authorisation.service';
import {ReadOnlyService} from '../../../../common/services/readOnly.service';
import {FilterDropdownMapDTO} from 'src/app/modules/common/models/FilterDropdownMapDTO';
import {ProcessingClassSubTypeDBMapping} from '../../../types/types';
import {EntityTable} from '../../../../common/types/entity-table.enum';
import {CustomDateRange, KeyValueMapping} from '../../../../common/types/common.types';
import {
  ClaimSearchCriteriaDialogComponent,
  SearchCriteriaControls
} from '../../dialogs/claim-search-criteria-dialog/claim-search-criteria-dialog.component';
import {
  UpfrontRejectionClaimDialogComponent
} from '../../dialogs/upfront-rejected-claims-dialog/upfront-rejection-claim-dialog.component';
import {OtherRequestBuilder} from '../../../../other/services/other-request-builder';
import {IntegrationErrorMessageDTO} from '../../../../common/models/IntegrationErrorMessageDTO';
import {IntegrationErrorMessageService} from '../../../../common/services/integration-error-message.service';
import {ReversalClaimDialogComponent} from '../../dialogs/revertal-claim-dialog/reversal-claim-dialog.component';
import {ProductStore} from '../../../../common/store/product.store';
import { PolicyService } from 'src/app/modules/ent/services/policy.service';
import { PolicyholderGroupService } from 'src/app/modules/ent/services/policyholder-group.service';
import { MemberOverviewDialogComponent } from 'src/app/modules/common/dialogs/member-overview-dialog/member-overview-dialog.component';

enum RoutePaths {
  ReviewClaim = 'review-claim',
  ReviewAuthorisation = 'review-authorisation',
}

enum ClaimLockStatus {
  Locked = 1,
  UnLocked = 0
}

enum TableColumns {
  Icons = 'icons',
  TransactionId = 'transactionId',
  TransactionType = 'transactionType',
  EffectiveDate = 'encounterStartDate',
  ProductName = 'productName',
  PolicyNumber = 'policyNumber',
  InsuredPersonName = 'insuredPersonName',
  ProviderName = 'providerName',
  PayTo = 'payTo',
  BilledAmount = 'billedAmount',
  ClaimedAmount = 'claimedAmount',
  BenefitAmount = 'benefitAmount',
  ProcessingStatus = 'processingStatus',
  AssessingStatus = 'assessingStatus',
}

const ALL_CLASSES = 'All Classes';
const ALL_SUB_CLASSES = 'All Sub Classes';
const ALL_TYPES = 'All Types';
const ALL_STATUSES = 'All Statuses';
const ALL_STAGES = 'All Stages';
const ALL_CREATORS = 'All Submitters';
const ALL_BENEFIT_FAMILIES = 'All Benefit Families';
const REJECTED = 'Rejected';

enum FilterByKeys {
  SubmittedBy = 'Submitted By',
  ProcessingClass = 'Processing Class',
  SubClass = 'Sub-Class',
  TransactionType = 'Transaction Type',
  AdjudicationStatus = 'Adjudication Status',
  ProcessingStage = 'Processing Stage',
  FamilyOfBenefit = 'Benefit Family',
  DateRange = 'Date Range',
  ChargeableCode = 'Chargeable Code',
  PracticeNumber = 'Practice Number',
  Product = 'Product'
}

@Component({
  selector: 'app-claims',
  templateUrl: './claims.component.html',
  styleUrls: [
    '../../../../../../styles/global/_management-styles.scss',
    '../../../../../../styles/global/_add-view-edit-styles.scss',
    './claims.component.scss'
  ],
  animations: [detailsExpand]
})
export class ClaimsComponent implements OnChanges, OnInit, OnDestroy, AfterViewChecked, AfterViewInit {

  readonly getStatusColor = getStatusColor;

  readonly dateFormat: string = DateUtil.dateFormat;
  readonly dateFormatWithoutSlashes: string = DateUtil.dateFormatWithoutSlashes;
  readonly dateTimeFormat: string = DateUtil.dateTimeFormat;
  readonly debounceTime: number = Common.DEBOUNCE_TIME;
  readonly destroyed$: Subject<boolean> = new Subject<boolean>();
  readonly tableColumns = TableColumns;
  readonly filters = FilterByKeys;
  readonly filterLabels: string[] = [...Object.values(FilterByKeys)];
  readonly temporarilyDisabledFilters: string[] = [
    FilterByKeys.ChargeableCode,
    FilterByKeys.SubClass
  ];
  readonly temporarilyDisabledFilterTooltip = 'This filter is temporarily disabled while we improve its performance.';
  readonly SearchCriteria = SearchCriteriaControls;
  readonly upfrontRejectionRouteIds = [
    'api.claim.request.allegra',
    'api.claim.request.general'
  ];

  readonly ClmFunctions = ClmFunctions;
  readonly ClaimType = ClaimType;
  readonly ProcessingStatus = ProcessingStatus;
  readonly TabLabel = TabLabel;

  readonly transactionsSearchFilters: FilterDropdownMapDTO[] = [
    {displayName: this.SearchCriteria.MemberIDNumber, filterName: 'insuredPersonByInsuredPersonId.nationalIdNumber', fieldMapPropertyName: 'insuredPersonByInsuredPersonId_1'},
    {displayName: this.SearchCriteria.MemberLastName, filterName: 'insuredPersonByInsuredPersonId.lastName', fieldMapPropertyName: 'insuredPersonByInsuredPersonId_2'},
    {displayName: this.SearchCriteria.PolicyNumber, filterName: 'insuredPersonByInsuredPersonId.cardNumber', fieldMapPropertyName: 'insuredPersonByInsuredPersonId_3'},
    {displayName: this.SearchCriteria.ReferenceNumber, filterName: 'referenceNumber', fieldMapPropertyName: 'referenceNumber'},
    {displayName: this.SearchCriteria.TransactionIDs, filterName: 'transactionId', fieldMapPropertyName: 'transactionId'},
    {displayName: this.SearchCriteria.TransactionNumber, filterName: 'externalTransactionNumber', fieldMapPropertyName: 'externalTransactionNumber'}
  ];

  readonly defaultSelectedSearchFilters: string[] = [
    this.SearchCriteria.MemberIDNumber,
    this.SearchCriteria.PolicyNumber
  ];

  @Input() defaultProcessingClass: string;
  @Input() isProviderPortal: boolean;

  @ContentChild('memberIconTpl') memberIconTpl: TemplateRef<any> | undefined;

  length = 100;
  pageSize = 10;
  pageNumber = 0;
  pageSizeOptions: number[] = [5, 10, 25];
  transactionExpandedRow: ClaimDisplayDTO | null;
  transactionDataSource: ClaimDisplayDTO[];
  transactionDisplayedColumns: string[] = Object.values(TableColumns);
  claimsSearchNameFormControl: FormControl;
  searchCriteriaFilterSelectionControl: FormControl;
  claimTypes: NameValueDTO[];
  createdByList: NameValueDTO[];
  adjudicationStatuses: NameValueDTO[];
  processingStages: NameValueDTO[];
  processingClasses: string[];
  processingSubClassesFuneral: NameValueDTO[];
  processingSubClassesMedical: NameValueDTO[];
  processingSubClassesAll: NameValueDTO[];
  familyOfBenefits: NameValueDTO[];
  processingClass: string;
  processingSubClass: string;
  disabledProcessingClass: boolean;
  claimType: string;
  createdBy: string;
  processingStage: string;
  familyOfBenefit: string;
  dateRange: CustomDateRange | null;
  tariffCodeList: string | null;
  practiceNumberFilter: string | null;
  adjudicationStatus: string;
  claimSearchTextChanged: Subject<string> = new Subject<string>();
  userFirstLastName = '';
  transactionTableControl: TableControl;
  externalMessageDataTableControl: TableControl;
  practiceNumber: string; // Note: username will act as practice no for General practitioners in Provider Portal
  defaultFiltersValues: string[] = this.filterLabels.filter((label: string) => (
    label !== FilterByKeys.SubClass &&
    label !== FilterByKeys.ChargeableCode &&
    label !== FilterByKeys.FamilyOfBenefit &&
    label !== FilterByKeys.ProcessingClass &&
    label !== FilterByKeys.ProcessingStage
  ));
  selectedFilters: KeyValueMapping<any> = {};
  upfrontRejectedClaims: number;
  searchValue: string; // Used as 'CorrelationId' for fetching "Upfront Rejected" claims
  parentForm: FormGroup;
  products: string[];

  private readonly productStore = inject(ProductStore);
  private previousUpfrontSearchValue = signal<string>("");

  @ViewChild('transactionPaginator', {static: false}) transactionPaginator: MatPaginator;

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private claimFilterCacheService: ClaimFilterCacheService,
    private changeDetect: ChangeDetectorRef,
    private claimService: ClaimService,
    private readOnlyService: ReadOnlyService,
    private lookupService: LookupService,
    private sharedService: SharedService,
    private sheetService: ClaimExcelSheetService,
    public datePipe: DatePipe,
    public timeElapsedFromNowPipe: TimeElapsedFromNowPipe,
    private formBuilder: FormBuilder,
    private userAuthorisationService: UserAuthorisationService,
    private integrationErrorMessageService: IntegrationErrorMessageService,
    private policyService: PolicyService,
    private policyholderGroupService: PolicyholderGroupService
  ) {
    this.transactionTableControl = new TableControl(
      this.length,
      this.pageSize,
      this.pageNumber,
      this.pageSizeOptions,
      'transactionId',
      'desc',
      'transactionId',
      ''
    );

    // page size and number is 1 and 0 respectively because we only require the total records this will remove uneccessary load on db execution
    this.externalMessageDataTableControl = new TableControl(
      this.length,
      1,
      0,
      this.pageSizeOptions,
      'id',
      'DESCENDING',
      '',
      ''
    );

    this.processingClasses = [ALL_CLASSES, 'Medical', 'Funeral'];
    this.createdBy = ALL_CREATORS;
    this.processingClass = ALL_CLASSES;
    this.processingSubClass = ALL_SUB_CLASSES;
    this.claimType = ALL_TYPES;
    this.processingStage = ALL_STAGES;
    this.adjudicationStatus = ALL_STATUSES;
    this.familyOfBenefit = ALL_BENEFIT_FAMILIES;
    this.processingSubClassesAll = [];
    this.processingSubClassesAll.unshift({value: 0, name: ALL_SUB_CLASSES});
  }

  ngAfterViewInit(): void {
    this.searchCriteriaFilterSelectionControl.setValue(this.defaultSelectedSearchFilters);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Note: Provider Portal
    if (changes.defaultProcessingClass) {
      this.processingClass = changes.defaultProcessingClass.currentValue;
    }
    if (changes.isProviderPortal) {
      this.isProviderPortal = changes.isProviderPortal.currentValue;
      this.filterDisplayedColumn();
    }
    this.disabledProcessingClass = true;
  }

  ngOnInit(): void {
    this.initFormControls();
    this.initSubscriptions();
    this.getListOfLookupValues()
      .pipe(
        switchMap(() => this.sharedService.getLoggedInUserProfile$()),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        this.practiceNumber = this.sharedService.getProfileLinkedEntityCode();
        this.userFirstLastName = this.sharedService.getLoggedInUserName();
        this.setFilterValue();
      });

    // Default Visible Filters
    this.defaultFiltersValues.forEach((filterKey: string) => {
      this.selectedFilters[filterKey] = true;
    });

  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  setPaginatorProperties(pageSize: number, pageIndex: number): void {
    if (this.transactionPaginator) {
      this.transactionPaginator.pageIndex = pageIndex;
      this.transactionPaginator.pageSize = pageSize;
    }
  }

  initFormControls(): void {
    this.claimsSearchNameFormControl = new FormControl(null);
    this.searchCriteriaFilterSelectionControl = new FormControl([], Validators.required);

    this.parentForm = this.formBuilder.group({
      dateRange: new FormControl(null),
      tariffCodeList: new FormControl(null),
      practiceNumber: new FormControl(null)
    });
  }

  initSubscriptions(): void {

    this.claimSearchTextChanged
      .pipe(
        debounceTime(this.debounceTime),
        switchMap((searchString: string | null) => of(searchString)),
        takeUntil(this.destroyed$)
      ).subscribe((searchString: string | null) => {
      this.transactionTableControl.filterValue = searchString;
      this.claimFilterCacheService.saveFilterValue(ClaimFilterKeys.SearchClaims, searchString);
    });

    if (this.isProviderPortal) {
      // This functionality is for PP for upload claims
      // therefore activate subscription for PP user
      this.sheetService.uploadClaim$AsObservable
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => {
          this.getListOfTransactions(this.transactionTableControl);
        });
    }
  }

  private getListOfIntegrationErrorMessages(tableControl: TableControl): Observable<PagedResponseDTO<IntegrationErrorMessageDTO>> {
    const pagedRequestDTO: PagedRequestDTO = OtherRequestBuilder.getIntegrationTblMessagesErrorPagedRequestDTO(tableControl);
    pagedRequestDTO.fieldMap = {};

    const correlationIdPagedFilterDTO = new PagedFilterDTO();
    correlationIdPagedFilterDTO.name = 'correlationId';
    correlationIdPagedFilterDTO.operator = '==';
    correlationIdPagedFilterDTO.value = this.claimsSearchNameFormControl.value;
    pagedRequestDTO.fieldMap[`correlationId`] = correlationIdPagedFilterDTO;

    const isConsumedPagedFilterDTO = new PagedFilterDTO();
    isConsumedPagedFilterDTO.name = 'isConsumed';
    isConsumedPagedFilterDTO.operator = '==';
    isConsumedPagedFilterDTO.value = '0';
    pagedRequestDTO.fieldMap[`isConsumed`] = isConsumedPagedFilterDTO;

    const routeIdPagedFilterDTO = new PagedFilterDTO();
    routeIdPagedFilterDTO.name = 'routeId';
    routeIdPagedFilterDTO.operator = 'in';
    routeIdPagedFilterDTO.value = this.upfrontRejectionRouteIds.join(',');
    pagedRequestDTO.fieldMap[`routeId`] = routeIdPagedFilterDTO;

    const dateTimePagedFilterDTO = new PagedFilterDTO();
    dateTimePagedFilterDTO.name = 'dateTime';
    dateTimePagedFilterDTO.operator = '>=';
    const numberOfDaysAgo = 14;
    const last14DaysDate: Date = getPastDateFromToday(numberOfDaysAgo);
    dateTimePagedFilterDTO.value = this.datePipe.transform(last14DaysDate, this.dateFormatWithoutSlashes);
    pagedRequestDTO.fieldMap[`dateTime`] = dateTimePagedFilterDTO;
    return this.integrationErrorMessageService.getListOfIntegrationErrorMessages(pagedRequestDTO);
  }

  setFilterValue(): void {
    this.claimFilterCacheService.getFilterValue()
      .pipe(
        switchMap((filterValues: ClaimFilterType) => {
          if (filterValues && !!Object.keys(filterValues)?.length) {
            return of(filterValues);
          }
          else {
            return defer(() => {
              this.getListOfTransactions(this.transactionTableControl);
              return EMPTY;
            });
          }
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe((filterValues: ClaimFilterType) => {
        const {
          searchValue,
          searchCriteria,
          tuneFilters,
          processingClass,
          claimType,
          createdBy,
          adjudicationStatus,
          processingStage,
          familyOfBenefit,
          pageNumber,
          pageSize,
          sortField,
          sortDirection,
          dateRange,
          practiceNumber,
          products
        } = filterValues;

        const filterValue = searchValue ?? '';
        this.searchValue = filterValue;
        this.claimsSearchNameFormControl.setValue(filterValue, {emitEvent: true}); // manually fetching list of transactions instead of valueChanges
        this.transactionTableControl.filterValue = filterValue;

        const selectedSearchCriteria: string[] = searchCriteria ?? this.defaultSelectedSearchFilters;
        this.searchCriteriaFilterSelectionControl.setValue(selectedSearchCriteria, {emitEvent: true});

        // Filters in DDN
        this.selectedFilters = this.withoutDisabledFilterSelections(tuneFilters ?? this.selectedFilters as KeyValueMapping<any>);

        // Setting to "ALL_<TYPE> will prevent to be added in fieldMap see list of transactions method.

        if (this.isProviderPortal) {
          this.processingClass = 'Medical'; // Filter cases on (Sorting/Pagination) for PP to show Medical claims only
        }
        else {
          this.processingClass = processingClass ?? ALL_CLASSES;
        }

        this.claimType = claimType ?? ALL_TYPES;
        this.adjudicationStatus = adjudicationStatus ?? ALL_STATUSES;
        this.processingStage = processingStage ?? ALL_STAGES;
        this.processingSubClass = ALL_SUB_CLASSES;
        this.createdBy = createdBy ?? ALL_CREATORS;
        this.familyOfBenefit = familyOfBenefit ?? ALL_BENEFIT_FAMILIES;
        this.dateRange = dateRange ??  null;
        this.parentForm.get('dateRange').setValue(this.dateRange);

        this.tariffCodeList = null;
        this.parentForm.get('tariffCodeList').setValue(this.tariffCodeList);

        this.practiceNumberFilter = practiceNumber ??  null;
        this.parentForm.get('practiceNumber').setValue(this.practiceNumberFilter);

        // Update Table control cached values
        this.transactionTableControl.pageSize = this.getAllowedPageSize(pageSize);
        this.transactionTableControl.pageNumber = pageNumber || this.transactionTableControl.pageNumber;
        this.transactionTableControl.sortField = sortField || this.transactionTableControl.sortField;
        this.transactionTableControl.sortDirection = sortDirection || this.transactionTableControl.sortDirection;
        this.getListOfTransactions(this.transactionTableControl);

        if (products?.length > 0) {
          this.products = [...products];
        }

        const shouldFetchUpfrontRejections = this.shouldFetchUpfrontRejections(searchValue)
          && searchValue !== this.previousUpfrontSearchValue();

        if (!this.shouldFetchUpfrontRejections(searchValue)) {
          this.upfrontRejectedClaims = 0;
          this.previousUpfrontSearchValue.set(null);
        }

        iif(
          () => shouldFetchUpfrontRejections,
          this.getListOfIntegrationErrorMessages(this.externalMessageDataTableControl).pipe(
            take(1),
            catchError(_ => of(null))
          ),
          of(null)
        ).subscribe((result: PagedResponseDTO<IntegrationErrorMessageDTO> | null) => {
            if (result) {
              this.upfrontRejectedClaims = result.resultList?.length || 0;
              this.previousUpfrontSearchValue.set(searchValue);
            }
          });
      });
  }

  openMemberDialog(row: ClaimDisplayDTO, event: MouseEvent) {
    event.stopPropagation();
    let policyNumber;
    let policyId;

    //TODO remove after policy store impl

    this.policyService.getPolicyBy(row.insuredPersonId, ProcessingClass.Medical)
        .pipe(
          tap((policyDTO) => {
            policyNumber = policyDTO?.number;
            policyId = policyDTO?.policyId;
          }),
          switchMap((policyDTO) => {
            const groupValue = policyDTO?.policyholderGroup?.value;
            return groupValue
              ? this.policyholderGroupService.getPolicyholderGroup(groupValue)
              : of(null);
          }),
        )
        .subscribe(group=> {
          this.dialog.open(MemberOverviewDialogComponent, {
          width: '80vw',
          height: '80vh',
          data: {
            personId: row.insuredPersonId,
            optionCode: row.productCode,
            membershipNumber: row.cardNumber,
            policyId: policyId,
            policyNumber: policyNumber,
            policyHoliderGroupId: group?.policyholderGroupId
          },
          panelClass: 'gd-member-overview-dialog',
        });
        });
  }

  private isPrincipalMember(row: ClaimDisplayDTO): boolean {
    return !row?.dependentNo || row.dependentNo === '00';
  }

  getMemberRelationshipLabel(row: ClaimDisplayDTO): string {
    return this.isPrincipalMember(row) ? 'Principal' : 'Dependant';
  }

  ngAfterViewChecked(): void {
    this.changeDetect.detectChanges();
  }

  processingClassSelected(value: string) {
    this.processingClass = value;
    this.claimFilterCacheService.saveFilterValue(ClaimFilterKeys.ProcessingClass, value);
  }

  onProcessingSubClassSelectionChanged(value: string) {
    if (this.isFilterTemporarilyDisabled(FilterByKeys.SubClass)) {
      return;
    }
    this.processingSubClass = value;
    this.claimFilterCacheService.saveFilterValue(ClaimFilterKeys.ProcessingSubClass, value);
  }

  claimTypeSelected(value: string) {
    this.claimType = value;
    this.claimFilterCacheService.saveFilterValue(ClaimFilterKeys.ClaimType, value);
  }

  onCreatedBySelectionChanged(value: string) {
    this.createdBy = value;
    this.claimFilterCacheService.saveFilterValue(ClaimFilterKeys.CreatedBy, value);
  }

  adjudicationStatusSelected(value: string) {
    this.adjudicationStatus = value;
    this.claimFilterCacheService.saveFilterValue(ClaimFilterKeys.AdjudicationStatus, value);
  }

  processingStageSelected(value: string): void {
    this.processingStage = value;
    this.claimFilterCacheService.saveFilterValue(ClaimFilterKeys.ProcessingStage, value);
  }

  familyOfBenefitSelected(value: string): void {
    if (this.isFilterTemporarilyDisabled(FilterByKeys.FamilyOfBenefit)) {
      return;
    }
    this.familyOfBenefit = value;
    this.claimFilterCacheService.saveFilterValue(ClaimFilterKeys.FamilyOfBenefit, value);
  }
  onDateRangeChanged(dateRange: CustomDateRange): void {
    this.dateRange = dateRange;
    this.claimFilterCacheService.saveFilterValue(ClaimFilterKeys.DateRange, this.dateRange);
  }

  onChargeableCodeChanged(value: string): void {
    if (this.isFilterTemporarilyDisabled(FilterByKeys.ChargeableCode)) {
      return;
    }
    this.tariffCodeList = value;
    this.claimFilterCacheService.saveFilterValue(ClaimFilterKeys.TariffCodeList, value);
  }

  onPracticeNumberChanged(value: string): void {
    this.practiceNumberFilter = value;
    this.claimFilterCacheService.saveFilterValue(ClaimFilterKeys.PracticeNumber, value);
  }

  onSelectedFiltersSelectionChanged(selectedFilters: KeyValueMapping<any>): void {
    const updatedFilters = {};
    selectedFilters = this.withoutDisabledFilterSelections(selectedFilters);

    if (!selectedFilters[FilterByKeys.SubmittedBy]) {
      updatedFilters[ClaimFilterKeys.CreatedBy] = ALL_CREATORS;
    }
    if (!selectedFilters[FilterByKeys.ProcessingClass]) {
      updatedFilters[ClaimFilterKeys.ProcessingClass] = ALL_CLASSES;
    }
    if (!selectedFilters[FilterByKeys.SubClass]) {
      updatedFilters[ClaimFilterKeys.ProcessingSubClass] = ALL_SUB_CLASSES;
    }
    if (!selectedFilters[FilterByKeys.TransactionType]) {
      updatedFilters[ClaimFilterKeys.ClaimType] = ALL_TYPES;
    }
    if (!selectedFilters[FilterByKeys.AdjudicationStatus]) {
      updatedFilters[ClaimFilterKeys.AdjudicationStatus] = ALL_STATUSES;
    }
    if (!selectedFilters[FilterByKeys.ProcessingStage]) {
      updatedFilters[ClaimFilterKeys.ProcessingStage] = ALL_STAGES;
    }
    if (!selectedFilters[FilterByKeys.FamilyOfBenefit]) {
      updatedFilters[ClaimFilterKeys.FamilyOfBenefit] = ALL_BENEFIT_FAMILIES;
    }

    if (!selectedFilters[FilterByKeys.DateRange]) {
      this.dateRange = null;
      updatedFilters[ClaimFilterKeys.DateRange] = null;
    }

    if (!selectedFilters[FilterByKeys.ChargeableCode]) {
      this.tariffCodeList = null;
      updatedFilters[ClaimFilterKeys.TariffCodeList] = null;
    }

    if (!selectedFilters[FilterByKeys.PracticeNumber]) {
      this.practiceNumberFilter = null;
      updatedFilters[ClaimFilterKeys.PracticeNumber] = null;
    }

    if (!selectedFilters[FilterByKeys.Product]) {
      this.products = null;
      updatedFilters[ClaimFilterKeys.Product] = null;
    }

    // Save updated filters and refresh the transaction list
    this.claimFilterCacheService.saveMultipleFilterValue({
      ...updatedFilters,
      [ClaimFilterKeys.TuneFilters]: selectedFilters
    });
  }

  isFilterTemporarilyDisabled(filter: FilterByKeys): boolean {
    return this.temporarilyDisabledFilters.includes(filter);
  }

  private withoutDisabledFilterSelections(selectedFilters: KeyValueMapping<any>): KeyValueMapping<any> {
    const enabledFilters = {...selectedFilters};

    this.temporarilyDisabledFilters.forEach((filter: string) => {
      delete enabledFilters[filter];
    });

    return enabledFilters;
  }

  onClickViewOrReviewClaim(event: MouseEvent, claim: ClaimDisplayDTO, isReadOnly: boolean) {
    event.preventDefault();
    event.stopPropagation();

    this.setReadOnlyTransactions(isReadOnly);
    this.navigateToReview(claim);
  }

  setReadOnlyTransactions(isReadOnly: boolean): void {
    this.readOnlyService.readOnlyTransactions = isReadOnly;
  }

  navigateToReview(claim: ClaimDisplayDTO): void {
    const route: RoutePaths = claim.transactionType === 'Claim' ? RoutePaths.ReviewClaim : RoutePaths.ReviewAuthorisation;
    if (this.isProviderPortal) {
      this.router.navigate([`/provider/${route}/${claim.transactionId}`]);
    }
    else {
      this.router.navigate([`/claim-management/${route}/${claim.transactionId}`]);
    }
  }

  private shouldFetchUpfrontRejections(searchValue: string | null): boolean {
    return /^\d+$/.test(searchValue ?? '')
      && this.searchCriteriaFilterSelectionControl?.value?.includes(this.SearchCriteria.PolicyNumber);
  }

  reverseClaim(event: MouseEvent, claim: ClaimDisplayDTO): void {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.dialog.open(ReversalClaimDialogComponent, {
      width: '45vw',
      data: {
        isPharmacyClaim: claim.processingSubClassId == ProcessingClassSubTypeDBMapping.Pharmacy
      }
    });

    dialogRef.afterClosed().subscribe((result: { reverseClaim: boolean, revertClaim: boolean}) => {
      if (result) {
        if (result?.reverseClaim) {
          this.handleReverseClaim(claim);
        }
        else if (result?.revertClaim) {
          this.handleRevertPaidClaim(claim);
        }
      }
    });

  }

  handleReverseClaim(claim: ClaimDisplayDTO): void {
    if (!claim.transactionId || !this.userFirstLastName) {
      return;
    }
    this.claimService.reverseClaim(claim.transactionId, this.userFirstLastName)
      .pipe(take(1))
      .subscribe(
        () => {
          this.sharedService.openSnackBar(`Claim ${claim.transactionId} reversed successfully`, 'Dismiss');
          this.getListOfTransactions(this.transactionTableControl);
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  handleRevertPaidClaim(claim: ClaimDisplayDTO): void {
    const workflowResultDTO: WorkflowResultDTO = new WorkflowResultDTO();
    workflowResultDTO.transactionId = claim.transactionId;
    workflowResultDTO.workflowStep = 'Assessed';
    workflowResultDTO.result = 'Pending';
    workflowResultDTO.isManualIntervention = true;
    workflowResultDTO.createdBy = this.userFirstLastName;

    this.claimService.revertPaidClaim(workflowResultDTO)
      .pipe(take(1))
      .subscribe(
        () => {
          this.sharedService.openSnackBar(`Claim ${claim.transactionId} reverted to pending status successfully`, 'Dismiss');
          this.getListOfTransactions(this.transactionTableControl);
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  onClickLockClaim(event: MouseEvent, claim: ClaimDisplayDTO): void {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: `Are you sure you want to lock transaction ${claim.transactionId}`
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const workflowResultDTO: WorkflowResultDTO = new WorkflowResultDTO();
        workflowResultDTO.transactionId = claim.transactionId;
        workflowResultDTO.workflowStep = claim.processingStatus;
        workflowResultDTO.result = claim.assessingStatus;
        workflowResultDTO.isManualIntervention = true;
        workflowResultDTO.createdBy = this.userFirstLastName;

        this.claimService.lockClaim(workflowResultDTO)
          .pipe(
            take(1),
          )
          .subscribe({
            next: () => {
              this.sharedService.openSnackBar(`Transaction ${claim.transactionId} is locked successfully`, 'Dismiss');
              this.getListOfTransactions(this.transactionTableControl);
            },
            error: (errorResponse) => {
              const error = errorResponse.error;
              this.sharedService.openErrorDialog(error.businessMessage);
            }
          });
      }
    });
  }

  onClickUnlockClaim(event: MouseEvent, claim: ClaimDisplayDTO): void {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: `Are you sure you want to unlock transaction ${claim.transactionId}`
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const workflowResultDTO: WorkflowResultDTO = new WorkflowResultDTO();
        workflowResultDTO.transactionId = claim.transactionId;
        workflowResultDTO.workflowStep = claim.processingStatus;
        workflowResultDTO.result = claim.assessingStatus;
        workflowResultDTO.isManualIntervention = true;
        workflowResultDTO.createdBy = this.userFirstLastName;

        this.claimService.unlockClaim(workflowResultDTO).subscribe(
          () => {
            this.sharedService.openSnackBar(`Transaction ${claim.transactionId} is unlocked successfully`, 'Dismiss');
            this.getListOfTransactions(this.transactionTableControl);
          },
          (errorResponse) => {
            const error = errorResponse.error;
            this.sharedService.openErrorDialog(error.businessMessage);
          }
        );
      }
    });
  }

  markClaimAsPaid(event: MouseEvent, claim: ClaimDisplayDTO): void {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.dialog.open(AddManualPaymentDialogComponent, {
      width: '40vw',
      data: {
        transactionId: claim.transactionId,
        paymentId: claim.paymentId,
        adjudicationStatus: claim.assessingStatus
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getListOfTransactions(this.transactionTableControl);
      }
    });

  }

  sortData($event: Sort): void {
    if (!$event.direction) {
      return;
    }
    if (this.transactionPaginator) {
      this.transactionPaginator.firstPage();
    }
    this.transactionTableControl.pageNumber = 0;
    if ($event.active === 'assessingStatus') {
      this.transactionTableControl.sortField = 'adjudicationStatusByAdjudicationStatusId.name';
    }
    else if ($event.active === 'processingStatus') {
      this.transactionTableControl.sortField = 'processingStatusByProcessingStatusId.name';
    }
    else if ($event.active === 'insuredPersonName') {
      this.transactionTableControl.sortField = 'insuredPersonByInsuredPersonId.lastName';
    }
    else if ($event.active === 'transactionType') {
      this.transactionTableControl.sortField = 'transactionTypeByTransactionTypeId.transactionType';
    }
    else if ($event.active === 'processingClass') {
      this.transactionTableControl.sortField = 'processingClassByProcessingClassId.className';
    }
    else if ($event.active === 'productName') {
      this.transactionTableControl.sortField = 'productByProductId.name';
    }
    else {
      this.transactionTableControl.sortField = $event.active;
    }
    this.transactionTableControl.sortDirection = $event.direction;
    this.claimFilterCacheService.saveMultipleFilterValue({
      [ClaimFilterKeys.SortField]: this.transactionTableControl.sortField,
      [ClaimFilterKeys.SortDirection]: this.transactionTableControl.sortDirection,
    });
  }

  pageChanged($event: PageEvent): void {
    // TODO Fix disable / re-enable of pagination
    // if(this.transactionPaginator != null) this.transactionPaginator.disabled = true;
    this.transactionTableControl.pageNumber = $event.pageIndex;
    this.transactionTableControl.pageSize = this.getAllowedPageSize($event.pageSize);
    this.claimFilterCacheService.saveMultipleFilterValue({
      [ClaimFilterKeys.PageNumber]: this.transactionTableControl.pageNumber,
      [ClaimFilterKeys.PageSize]: this.transactionTableControl.pageSize,
    });
  }

  getCaseUrl(row: ClaimDisplayDTO): string {
    return ClmFunctions.getCaseUrl(row, this.router);
  }

  viewClaimDetails(event: MouseEvent, transactionId: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.dialog.open(ActivityHistoryDialogComponent, {
      width: '95%',
      maxWidth: 'none',
      height: '80vh',
      data: {
        id: transactionId,
        field: 'encounterByEncounterId.transactionByTransactionId.transactionId',
        isProviderPortal: this.isProviderPortal
      }
    });
  }

  isAuthorisedToShowLockTransaction(row: ClaimDisplayDTO): boolean {
    return row.isLocked === ClaimLockStatus.UnLocked && this.hasValidRoleAndProcessingStatus(row);
  }

  isAuthorisedToShowUnlockTransaction(row: ClaimDisplayDTO): boolean {
    return row.isLocked === ClaimLockStatus.Locked && this.hasValidRoleAndProcessingStatus(row);
  }

  hasValidRoleAndProcessingStatus(row: ClaimDisplayDTO): boolean {
    const isProcessingStatusMatched = row.processingStatus == ProcessingStatus.AuthorisationComplete
      || row.processingStatus == ProcessingStatus.ReadyForSettlement;
    return isProcessingStatusMatched && this.userAuthorisationService.hasRoleClaimsAssessor();
  }

  isAuthorisedToUnlockClaim(): boolean {
    return this.userAuthorisationService.hasRoleTransactionTeamLeader();
  }

  allowReview(row): boolean {
    // Review is opposite of View in terms of Status
    // claim is locked, it should not be editable for both (pp/normal) portal
    const isClaimLocked: boolean = row.isLocked === ClaimLockStatus.Locked;
    return !this.readOnlyService.isTransactionReadOnly(row.processingStatus as ProcessingStatus) && !isClaimLocked;
  }

  allowWarning(row: ClaimDisplayDTO): boolean {
    return row.details.showDangerIcon || row.details.showMissingBankAccountIcon || row.details.showPaymentIcon || row.details.showWarningIcon
  }

  onClickOpenErrorDialog(row: ClaimDisplayDTO): void {
    let warningList = [];

    if (row.details.showDangerIcon || row.details.showWarningIcon) {
      warningList.push(this.getElapsedDaysTooltip(row.details))
    }
    if (row.details.showMissingBankAccountIcon) {
      warningList.push(this.getMissingBankAccountTooltip(row.details))
    }
    if (row.details.showPaymentIcon) {
      warningList.push(this.getPaymentTooltip(row.details))
    }

    this.sharedService.openErrorDialog(warningList.join("\n"));

  }

  allowReverse(row: ClaimDisplayDTO): boolean {
    const hasReverseRole = this.userAuthorisationService.hasRoleClaimReverser();
    const isAllowedToReverse = (
      row.processingStatus == ProcessingStatus.Paid ||
      row.processingStatus == ProcessingStatus.PaymentRunComplete ||
      row.processingStatus == ProcessingStatus.ReadyForSettlement ||
      row.processingStatus == ProcessingStatus.AuthorisationClaimed ||
      row.processingStatus == ProcessingStatus.UpfrontPaid ||
      (row.transactionType == ClaimType.CLAIM && row.processingSubClassId == ProcessingClassSubTypeDBMapping.Pharmacy)
    );

    return isAllowedToReverse && hasReverseRole;
  }

  getElapsedDaysTooltip(details: any): string {
    return `This claim was received ${details?.claimElapsedDays} days ago.`;
  }

  getMissingBankAccountTooltip(details: any): string {
    return `The payee's bank account details are missing.`;
  }

  getPaymentTooltip(details: any): string {
    return details.paymentId == null || details.isManualPayment == 1 ? 'Edit the payment details of a manually paid claim.' : 'Manually mark the claim as paid';
  }

  getProcessingStatus(row: ClaimDisplayDTO): string {
    switch (row.processingStatus) {
    case ProcessingStatus.BenefitAllocated:
      if (row.assessingStatus === REJECTED) {
        return 'No Benefit Allocated';
      }
      break;
    case ProcessingStatus.Priced:
      if (row.assessingStatus === REJECTED) {
        return 'Not Priced';
      }
      break;
    case ProcessingStatus.Paid:
      if (row.paymentDate != null) {
        return 'Paid ' + this.datePipe.transform(new Date(row.paymentDate), this.dateFormat);
      }
      break;
    }

    return row.processingStatus;
  }

  convertNumberToDate(milliseconds: number): Date {
    return milliseconds != null ? new Date(milliseconds) : null;
  }

  onInputChangedSearchTransactions($event: Event): void {
    const inputElement: HTMLInputElement = $event.target as HTMLInputElement;

    if (this.transactionPaginator != null) {
      this.transactionPaginator.firstPage();
    }
    this.claimSearchTextChanged.next(inputElement.value);
  }

  clearSearchTransactionsFilter(): void {
    if (this.transactionPaginator) {
      this.transactionPaginator.pageIndex = 0;
      this.transactionPaginator.pageSize = 10;
    }
    this.claimsSearchNameFormControl.patchValue(null);
    this.claimSearchTextChanged.next(null);
  }

  getSubClasses(): NameValueDTO[] {
    let subClasses: NameValueDTO[] = [];
    const processingSubClassesMedical: NameValueDTO[] = this.processingSubClassesMedical != null ? this.processingSubClassesMedical : [];
    const processingSubClassesFuneral: NameValueDTO[] = this.processingSubClassesFuneral != null ? this.processingSubClassesFuneral : [];
    const processingSubClassesAll: NameValueDTO[] = this.processingSubClassesAll != null ? this.processingSubClassesAll : [];

    if (this.processingClass === ProcessingClassDisplayValue.Medical) {
      subClasses = processingSubClassesMedical;
    }
    else if (this.processingClass === ProcessingClassDisplayValue.Funeral) {
      subClasses = processingSubClassesFuneral;
    }
    else if (this.processingClass === ALL_CLASSES) {
      subClasses = processingSubClassesAll;
    }

    return subClasses;
  }

  allowView(row: ClaimDisplayDTO): boolean {
    return this.readOnlyService.isTransactionReadOnly(row.processingStatus as ProcessingStatus);
  }

  onClickShowUpfrontRejectedClaimDialog($event: MouseEvent): void {
    $event.preventDefault();
    $event.stopPropagation();

    this.dialog.open(UpfrontRejectionClaimDialogComponent, {
      data: {
        correlationId: this.searchValue
      }
    });
  }

  onClickShowSearchCriteriaDialog($event: MouseEvent): void {
    $event.preventDefault();
    $event.stopPropagation();

    const dialogRef: MatDialogRef<ClaimSearchCriteriaDialogComponent> = this.dialog.open(ClaimSearchCriteriaDialogComponent, {
      width: '30vw',
      data: this.generateDialogData()
    });

    dialogRef
      .afterClosed()
      .subscribe((searchCriterFilters: any): void => {
        if (!searchCriterFilters) {
          return;
        }
        const selectedCriteria: string[] = this.getSelectedFilters(searchCriterFilters);
        this.claimFilterCacheService.saveFilterValue(ClaimFilterKeys.SearchCriteria, selectedCriteria);
      });
  }

  generateDialogData(): object {
    let dialogData = {};
    Object
      .keys(this.SearchCriteria)
      .forEach(key => {
        dialogData[key] = !!this.searchCriteriaFilterSelectionControl?.value?.includes(this.SearchCriteria[key]);
      });
    dialogData = {
      ...dialogData,
      isAllowedToEdit: true,
    };
    return dialogData;
  }

  getSelectedFilters(searchCriteriaFilters: SearchCriteriaControls): string[] {
    const selectedCriteria: string[] = [];
    Object
      .keys(SearchCriteriaControls)
      .forEach(key => {
        if (searchCriteriaFilters.hasOwnProperty(key) && searchCriteriaFilters[key]) {
          selectedCriteria.push(SearchCriteriaControls[key]);
        }
      });

    return selectedCriteria;
  }

  get selectedSearchCriteriaCount(): number {
    return this.searchCriteriaFilterSelectionControl?.value?.length ?? 0;
  }

  getListOfTransactions(tableControl: TableControl): void {
    this.claimService.getListOfClaims(this.getPagedRequestDTO(tableControl))
      .pipe(
        take(1),
      )
      .subscribe({
        next: (response: PagedResponseDTO<ClaimDisplayDTO>) => {
          if (response.resultList.length) {
            response.resultList.forEach((item: ClaimDisplayDTO) => {
              const timeFromNow: string = this.timeElapsedFromNowPipe.transform(item.receiveDate);
              item.details = {};
              this.setExtraDetails(timeFromNow, item);
            });
          }

          this.transactionDataSource = response.resultList;
          this.transactionTableControl.length = getPaginatorLengthFromPagedResponse(
            response,
            tableControl.pageNumber,
            tableControl.pageSize
          );
          this.cdr.detectChanges();
          this.setPaginatorProperties(this.transactionTableControl.pageSize, this.transactionTableControl.pageNumber);
        },
      error: (errorResponse) => {
        const error = errorResponse.error;
        this.sharedService.openErrorDialog(error.businessMessage);
      }}
    );
  }

  onProductChanged(event: string[]): void {
    if (event?.length == 0) {
      return;
    }

    this.transactionPaginator?.firstPage();
    this.transactionTableControl.pageNumber = 0;
    this.products = event;
    this.claimFilterCacheService.saveMultipleFilterValue({
      pageNumber: 0,
      products: [...event]
    });
    this.getListOfTransactions(this.transactionTableControl);
  }

  private setExtraDetails(timeFromNow: string, item: ClaimDisplayDTO): void {
    if (Number.isInteger(Number(timeFromNow))) {
      const elapsedDays: number = Number(timeFromNow);
      item.details.claimElapsedDays = timeFromNow;
      this.setIconBasedOnElapsedDays(item, elapsedDays);
    }
    this.setBankIcon(item);
    this.setPaymentIcon(item);
  }

  private setIconBasedOnElapsedDays(item: ClaimDisplayDTO, elapsedDays: number): void {
    if (
      !(item.processingStatus == ProcessingStatus.Paid ||
        item.processingStatus == ProcessingStatus.AuthorisationClaimed ||
        item.processingStatus == ProcessingStatus.AuthorisationComplete ||
        item.processingStatus == ProcessingStatus.UpfrontPaid
      )
    ) {
      if (elapsedDays >= 15 && elapsedDays < 20) {
        item.details.showWarningIcon = true;
      }
      else if (elapsedDays >= 20 && elapsedDays <= 30) {
        item.details.showDangerIcon = true;
      }
      // If rejected for more than 30 days, remove the icon
      else if (elapsedDays > 30) {
        item.details.showDangerIcon = item.assessingStatus !== REJECTED;
      }
    }
  }

  private setBankIcon(item: ClaimDisplayDTO): void {
    item.details.showMissingBankAccountIcon = false;
    if (item.transactionType == ClaimType.CLAIM && item.processingStatus === ProcessingStatus.ReadyForSettlement) {
      if (
        ClmFunctions.getPayToAcronym(item.payTo) === PayToAcronymEnum.Estate ||
        ClmFunctions.getPayToAcronym(item.payTo) === PayToAcronymEnum['Nominated Beneficiary'] ||
        ClmFunctions.getPayToAcronym(item.payTo) === PayToAcronymEnum['Alternative Beneficiary']
      ) {
        if (item.beneficiaryBankAccount == null) {
          item.details.showMissingBankAccountIcon = true;
        }
      }
      else if (ClmFunctions.getPayToAcronym(item.payTo) == PayToAcronymEnum.Beneficiary) {
        if (item.insuredPersonBankAccount == null) {
          item.details.showMissingBankAccountIcon = true;
        }
      }
      else if (ClmFunctions.getPayToAcronym(item.payTo) == PayToAcronymEnum.Provider) {
        if (item.providerBankAccount == null) {
          item.details.showMissingBankAccountIcon = true;
        }
      }
    }
  }

  private setPaymentIcon(item: ClaimDisplayDTO): void {
    const hasRoleFinanceTeamLeader: boolean = this.userAuthorisationService.hasRoleFinancialTeamLeader();
    item.details.showPaymentIcon = hasRoleFinanceTeamLeader && item.assessingStatus === AdjudicationStatus.Accepted &&
      (
        item.processingStatus === ProcessingStatus.ReadyForSettlement ||
        item.processingStatus === ProcessingStatus.PaymentRunComplete ||
        item.processingStatus === ProcessingStatus.Paid
      ) &&
      item.isManualPayment === 1;
  }

  private filterDisplayedColumn(): void {
    if (this.isProviderPortal) {
      this.transactionDisplayedColumns = this.transactionDisplayedColumns.filter((item: string) => item !== 'elapsedDays');
    }
  }

  private getListOfLookupValues(): Observable<void> {
    const lookupEntities: string[] = [
      'transaction_type',
      'adjudication_status',
      'processing_status',
      'processing_sub_class_medical',
      'processing_sub_class_funeral',
      'transaction_created_by',
      'family_of_benefit'
    ];

    const requests: Observable<NameValueDTO[]>[] = lookupEntities.map((entity: string) =>
      this.lookupService.getListOfLookupValues(entity)
    );

    return forkJoin(requests).pipe(
      take(1),
      catchError((errorResponse: any) => {
        const error = errorResponse.error;
        this.sharedService.openErrorDialog(error.businessMessage);
        return of([]);
      }),
      tap((responses: NameValueDTO[][]) => {
        this.handleLookupValuesResponses(responses);
      }),
      mapTo(null)
    );
  }

  private handleLookupValuesResponses(responses: NameValueDTO[][]): void {
    this.claimTypes = this.updateLookupWithDefault(responses[0], ALL_TYPES);
    this.adjudicationStatuses = this.updateLookupWithDefault(responses[1], ALL_STATUSES);
    this.processingStages = this.updateLookupWithDefault(responses[2], ALL_STAGES);
    this.processingSubClassesMedical = this.updateLookupWithDefault(responses[3], ALL_SUB_CLASSES);
    this.processingSubClassesFuneral = this.updateLookupWithDefault(responses[4], ALL_SUB_CLASSES);
    this.processingSubClassesAll = this.updateLookupArrayWithDefault([responses[3], responses[4]], ALL_SUB_CLASSES);
    this.createdByList = this.updateLookupWithDefault(responses[5], ALL_CREATORS);
    this.familyOfBenefits = this.updateLookupWithDefault(responses[6], ALL_BENEFIT_FAMILIES);
  }

  private updateLookupWithDefault(response: NameValueDTO[] | null, defaultName: string): NameValueDTO[] {
    const processedArray: NameValueDTO[] = [...(response || [])];
    processedArray.unshift({ value: 0, name: defaultName });
    return processedArray;
  }

  private updateLookupArrayWithDefault(lists: NameValueDTO[][], defaultName: string): NameValueDTO[] {
    const concatenatedList: NameValueDTO[] = ([] as NameValueDTO[]).concat(...lists);
    concatenatedList.unshift({value: 0, name: defaultName});
    return concatenatedList;
  }

  private getPagedRequestDTO(tableControl: TableControl): PagedRequestDTO {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblTransaction;
    pagedRequestDTO.pageNumber = tableControl.pageNumber;
    pagedRequestDTO.pageSize = this.getAllowedPageSize(tableControl.pageSize);
    pagedRequestDTO.sortField = tableControl.sortField;
    pagedRequestDTO.sortDirection = getSortDirection(tableControl.sortDirection);

    pagedRequestDTO.fieldMap = {};

    if (this.isProviderPortal) {
      // Note: We only want external provider to use their on claims ONLY!!
      const practiceNumberPagedFilterDTO = new PagedFilterDTONew();
      practiceNumberPagedFilterDTO.name = 'practiceNumber';
      practiceNumberPagedFilterDTO.value = this.practiceNumber;
      pagedRequestDTO.fieldMap[`practiceNumber`] = practiceNumberPagedFilterDTO;
    }

    if (this.processingClass !== ALL_CLASSES) {
      pagedRequestDTO.fieldMap[`processingClassByProcessingClassId`] = this.getFieldMapValue('processingClassByProcessingClassId.className', this.processingClass);
    }

    if (!this.isFilterTemporarilyDisabled(FilterByKeys.SubClass) && this.processingSubClass !== ALL_SUB_CLASSES) {
      const subClass = this.getSubClasses().find((item: NameValueDTO) => item.name === this.processingSubClass);
      if (subClass != null) {
        pagedRequestDTO.fieldMap[`processingSubClassByProcessingSubClassId`] = this.getFieldMapValue('processingSubClassByProcessingSubClassId.processingSubClassId', String(subClass.value));
      }
    }

    if (this.claimType !== ALL_TYPES) {
      pagedRequestDTO.fieldMap[`transactionTypeByTransactionTypeId`] = this.getFieldMapValue('transactionTypeByTransactionTypeId.transactionType', this.claimType);
    }

    if (this.adjudicationStatus !== ALL_STATUSES) {
      pagedRequestDTO.fieldMap[`adjudicationStatusByAdjudicationStatusId`] = this.getFieldMapValue('adjudicationStatusByAdjudicationStatusId.name', this.adjudicationStatus);
    }

    if (this.processingStage !== ALL_STAGES) {
      pagedRequestDTO.fieldMap[`processingStatusByProcessingStatusId`] = this.getFieldMapValue('processingStatusByProcessingStatusId.name', this.processingStage);
    }

    if (this.createdBy !== ALL_CREATORS) {
      pagedRequestDTO.fieldMap[`createdBy`] = this.getFieldMapValue('createdBy', this.createdBy);
    }

    if (!this.isFilterTemporarilyDisabled(FilterByKeys.FamilyOfBenefit) && this.familyOfBenefit !== ALL_BENEFIT_FAMILIES) {
      // An unresolvable cached family name sends id 0 (matches nothing) so the filter fails closed like the
      // name-based filter it replaced, instead of silently returning every family
      const family = this.familyOfBenefits.find((item: NameValueDTO) => item.name === this.familyOfBenefit);
      pagedRequestDTO.fieldMap[`familyOfBenefitId`] = this.getFieldMapValue('familyOfBenefitId', String(family?.value ?? 0), 'in');
    }

    if (this.dateRange?.start && this.dateRange?.end) {
      const startDateStr = this.formatDate(this.dateRange.start, this.dateFormat);
      const endDateStr = this.formatDate(this.dateRange.end, this.dateFormat);

      pagedRequestDTO.fieldMap[`encounterStartDate_1`] = this.getFieldMapValue('encountersByTransactionId.startDate', startDateStr, '>=');
      pagedRequestDTO.fieldMap[`encounterStartDate_2`] = this.getFieldMapValue('encountersByTransactionId.startDate', endDateStr, '<=');
    }

    const tariffCodeList = this.parentForm.get('tariffCodeList').value;
    if (!this.isFilterTemporarilyDisabled(FilterByKeys.ChargeableCode) && tariffCodeList) {
      pagedRequestDTO.fieldMap[`tariffCodeList`] = this.getFieldMapValue('tariffCodeList', tariffCodeList);
    }

    const practiceNumberFilter = this.parentForm.get('practiceNumber').value;
    if (practiceNumberFilter) {
      pagedRequestDTO.fieldMap[`practiceNumber`] = this.getFieldMapValue('practiceNumber', practiceNumberFilter);
    }

    const productsFilter = this.productStore.buildProductFilter(this.products, 'productByProductId.productCode');
    if (productsFilter) {
      pagedRequestDTO.fieldMap['productByProductId'] = productsFilter;
    }

    if (this.transactionTableControl != null && this.transactionTableControl.filterValue != null && this.transactionTableControl.filterValue.trim() !== '') {
      const fieldMapFilter = {};
      const searchValue = this.transactionTableControl.filterValue.trim();
      const isNumericSearch = /^\d+$/.test(searchValue) || isValidCommaSeparatedNumberList(searchValue);
      const textCriteria: string[] = [
        this.SearchCriteria.MemberLastName
      ];
      const numericCriteria: string[] = [
        this.SearchCriteria.MemberIDNumber,
        this.SearchCriteria.PolicyNumber,
        this.SearchCriteria.TransactionIDs
      ];

      this.searchCriteriaFilterSelectionControl.value?.forEach((searchCriteria: string) => {
        const shouldExcludeSearchCriteria = (isNumericSearch && textCriteria.includes(searchCriteria))
          || (!isNumericSearch && numericCriteria.includes(searchCriteria));

        if (
          !shouldExcludeSearchCriteria &&
          (searchCriteria !== this.SearchCriteria.TransactionIDs || isValidCommaSeparatedNumberList(searchValue))
        ) {
          const filterType: FilterDropdownMapDTO = this.transactionsSearchFilters.find((filterItem: FilterDropdownMapDTO) => (
            filterItem.displayName.includes(searchCriteria)
          ));
          fieldMapFilter[filterType.fieldMapPropertyName] = filterType.filterName;
        }
      });

      pagedRequestDTO.fieldMap = {...pagedRequestDTO.fieldMap, ...this.addFilterValuesToFieldMap(fieldMapFilter)};

      if (pagedRequestDTO.fieldMap[`transactionId`]) {
        pagedRequestDTO.fieldMap[`transactionId`].operator = 'in';
      }

    }
    return pagedRequestDTO;
  }

  private getAllowedPageSize(pageSize: number | null | undefined): number {
    return pageSize != null && this.pageSizeOptions.includes(pageSize) ? pageSize : this.pageSize;
  }

  private addFilterValuesToFieldMap(fieldMapFilter: object): object {
    const fieldMap = {};
    const fieldMapKey = Object.keys(fieldMapFilter);

    for (let i = 0; i < fieldMapKey.length; i++) {
      const pagedFilterDTO = new PagedFilterDTONew();
      pagedFilterDTO.name = fieldMapFilter[fieldMapKey[i]];
      pagedFilterDTO.value = this.transactionTableControl.filterValue.trim();

      if (fieldMapKey.length !== 1) {
        const isFirst = i === 0;
        const isLast = i === fieldMapKey.length - 1;
        pagedFilterDTO.logicGate = isFirst ? 'AND' : 'OR';
        pagedFilterDTO.bracket = isFirst ? '(' : (isLast ? ')' : '');
      }

      fieldMap[fieldMapKey[i]] = pagedFilterDTO;
    }

    return fieldMap;
  }

  private getFieldMapValue(name: string, value: string, operator: string = '=='): PagedFilterDTO {
    const pagedFilterDTO = new PagedFilterDTONew();
    pagedFilterDTO.name = name;
    pagedFilterDTO.operator = operator;
    pagedFilterDTO.value = value;
    return pagedFilterDTO;
  }

  private formatDate(date: Date, format: string): string {
    return this.datePipe.transform(date, format);
  }
}
